import { useEffect, useMemo, useState } from 'react';
import { Delete, Phone } from 'lucide-react';
import { Button } from '@/components/atoms/button';
import { Input } from '@/components/atoms/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/atoms/popover';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/atoms/select';
import {
  useCallState,
  useRegistrationState,
  useVoipActions,
  useCallerNumber,
} from '@/stores/voip.store';
import { usePhoneNumbers } from '@/lib/messaging/hooks';
import { AudioSettings } from '@/components/voip/AudioSettings';
import { useAuthPermissions } from '@/stores/auth.store';
import { cn } from '@/lib/utils';
import { formatPhoneDisplay } from '@/lib/contacts/phone';

const KEYS: { d: string; sub?: string }[] = [
  { d: '1' },
  { d: '2', sub: 'ABC' },
  { d: '3', sub: 'DEF' },
  { d: '4', sub: 'GHI' },
  { d: '5', sub: 'JKL' },
  { d: '6', sub: 'MNO' },
  { d: '7', sub: 'PQRS' },
  { d: '8', sub: 'TUV' },
  { d: '9', sub: 'WXYZ' },
  { d: '*' },
  { d: '0', sub: '+' },
  { d: '#' },
];

interface DialerTriggerProps {
  mobile?: boolean;
}

/**
 * Outbound-call panel opened from the utility rail / mobile bar. Close-style:
 * a Caller-ID picker on top, a clean keypad, and a prominent call action.
 * Hidden while a call is in progress (the in-call HUD takes over).
 */
export function DialerTrigger({ mobile = false }: DialerTriggerProps) {
  const callState = useCallState();
  const registration = useRegistrationState();
  const callerNumber = useCallerNumber();
  const { startCall, setCallerNumber } = useVoipActions();
  const [open, setOpen] = useState(false);
  const [number, setNumber] = useState('');

  // NOT-504: the dialer needs the tenant's outbound numbers (messages:view). A
  // role without it (e.g. accountant) can't place calls, so hide the trigger and
  // skip the phone-numbers fetch rather than firing a guaranteed 403.
  const canUseDialer = useAuthPermissions().includes('messages:view');
  const phoneNumbersQuery = usePhoneNumbers({ status: 'ACTIVE' }, { enabled: canUseDialer });
  // Memoised because the effect below depends on it. Filtering inline produced
  // a new array on EVERY render, so the effect re-ran every render, and with a
  // store write inside it the dialer re-rendered in a loop — which kept
  // re-requesting the tenant's numbers several times a second and, since a
  // browser allows only six connections per origin, starved every other call
  // on the page. That is what made Contacts and Locations time out.
  const lines = useMemo(
    () => (phoneNumbersQuery.data?.data ?? []).filter((n) => n.e164 && n.purpose !== 'AUTOMATION'),
    [phoneNumbersQuery.data],
  );

  // Default the Caller-ID picker to whatever register() resolved (or the first line).
  useEffect(() => {
    if (!callerNumber && lines[0]?.e164) setCallerNumber(lines[0].e164);
  }, [callerNumber, lines, setCallerNumber]);

  if (!canUseDialer) return null;
  if (callState !== 'idle') return null;

  // A caller ID is mandatory: without it startCall() omits callerNumber and
  // Telnyx rejects outbound setup ("CALL DOES NOT EXIST"). Gate the call on it
  // so the button/Enter can't fire when no active number is available.
  const ready = registration === 'registered' && Boolean(callerNumber);
  const call = () => {
    const dest = number.trim();
    if (!dest || !ready) return;
    startCall(dest);
    setNumber('');
    setOpen(false);
  };

  const statusLabel =
    registration === 'registering'
      ? 'Connecting…'
      : registration === 'failed'
        ? 'Phone unavailable'
        : registration === 'registered'
          ? 'Ready'
          : 'Not connected';

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          size={mobile ? 'default' : 'icon'}
          variant="ghost"
          className={cn(
            mobile
              ? 'h-14 flex-1 flex-col gap-0.5 rounded-none px-0 py-1 text-muted-foreground'
              : 'h-9 w-9',
          )}
          aria-label="Open dialer"
        >
          <Phone className={mobile ? 'h-5 w-5' : 'h-[18px] w-[18px]'} />
          {mobile ? <span className="text-[10px] font-medium">Call</span> : null}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align={mobile ? 'center' : 'end'}
        side={mobile ? 'top' : 'bottom'}
        sideOffset={8}
        className="max-h-[85vh] w-80 overflow-y-auto p-0"
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b px-3 py-2">
          <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
            Phone
          </span>
          <span className="flex items-center gap-1.5 text-[11px] text-muted-foreground">
            <span
              className={cn(
                'inline-flex h-1.5 w-1.5 rounded-full',
                ready
                  ? 'bg-emerald-500'
                  : registration === 'registering'
                    ? 'bg-amber-500'
                    : 'bg-muted-foreground/40',
              )}
            />
            {statusLabel}
          </span>
        </div>

        {/* Caller ID */}
        <div className="border-b px-3 py-2">
          <label className="mb-1 block text-[11px] font-medium text-muted-foreground">
            Caller ID
          </label>
          {lines.length > 0 ? (
            <Select value={callerNumber ?? undefined} onValueChange={setCallerNumber}>
              <SelectTrigger className="h-8 w-full text-sm">
                <SelectValue placeholder="Select a number" />
              </SelectTrigger>
              <SelectContent>
                {lines.map((n) => (
                  <SelectItem key={n.id} value={n.e164}>
                    <span className="flex items-center gap-2">
                      <span className="font-medium tabular-nums">{formatPhoneDisplay(n.e164)}</span>
                      {n.label ? (
                        <span className="text-xs text-muted-foreground">· {n.label}</span>
                      ) : null}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          ) : (
            <div className="rounded-md border border-dashed px-3 py-1.5 text-xs text-muted-foreground">
              No active number to call from.
            </div>
          )}
        </div>

        {/* Audio settings */}
        <div className="border-b px-3 py-2">
          <AudioSettings />
        </div>

        {/* Dial + keypad */}
        <div className="flex flex-col gap-2 px-3 py-2.5">
          <Input
            value={number}
            onChange={(e) => setNumber(e.target.value)}
            placeholder="Dial a number…"
            inputMode="tel"
            aria-label="Phone number"
            className="h-11 text-center text-base tracking-wide tabular-nums"
            onKeyDown={(e) => {
              if (e.key === 'Enter') call();
            }}
          />

          <div className="grid grid-cols-3 gap-1.5">
            {KEYS.map(({ d }) => (
              <button
                key={d}
                type="button"
                onClick={() => setNumber((n) => n + d)}
                aria-label={`Dial ${d}`}
                className="flex h-9 items-center justify-center rounded-lg bg-muted/50 text-base font-medium text-foreground transition-colors hover:bg-accent active:scale-95"
              >
                {d}
              </button>
            ))}
          </div>

          <div className="flex items-center justify-between gap-3 pt-0.5">
            <span className="w-9" />
            <Button
              onClick={call}
              disabled={!number.trim() || !ready}
              aria-label="Call"
              className="h-12 w-12 rounded-full bg-emerald-600 p-0 text-white shadow-sm hover:bg-emerald-700 disabled:opacity-40"
            >
              <Phone className="h-5 w-5" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setNumber((n) => n.slice(0, -1))}
              disabled={!number}
              aria-label="Backspace"
              className="h-9 w-9 text-muted-foreground"
            >
              <Delete className="h-[18px] w-[18px]" />
            </Button>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
